import { useEffect, useMemo, useRef } from 'react';
import { Edge } from 'reactflow';
import {
  CanvasElementState,
  CanvasZIndex,
} from '@components/MainStage/canvasStates';
import useHover from '@components/MainStage/hover.store';

export const resolveEdgeZIndex = (
  edge: Edge,
  hoveredEdgeId?: string | null,
): number => {
  if (edge.data?.canvas?.state === CanvasElementState.Disabled) {
    return CanvasZIndex.DisabledLine;
  }

  if (edge.id === hoveredEdgeId || edge.selected) {
    return CanvasZIndex.FocusedLine;
  }

  return CanvasZIndex.Line;
};

/**
 * Assigns each edge its z-index layer, preserving the identity of edges whose
 * layer did not change so React Flow can skip them.
 *
 * The cache of previous derivations is committed in an effect rather than
 * during the memo: a render React discards must not become the baseline the
 * next render compares against.
 */
const useEdgeLayers = (edges: Edge[]): Edge[] => {
  const hoveredEdgeId = useHover((state) => state.hoveredEdgeId);
  const derivedCache = useRef(
    new Map<string, { source: Edge; derived: Edge }>(),
  );

  const { result, nextCache } = useMemo(() => {
    const cache = new Map<string, { source: Edge; derived: Edge }>();

    const derivedEdges = edges.map((edge) => {
      const zIndex = resolveEdgeZIndex(edge, hoveredEdgeId);
      const cached = derivedCache.current.get(edge.id);

      const derived =
        cached && cached.source === edge && cached.derived.zIndex === zIndex
          ? cached.derived
          : { ...edge, zIndex };

      cache.set(edge.id, { source: edge, derived });

      return derived;
    });

    return { result: derivedEdges, nextCache: cache };
  }, [edges, hoveredEdgeId]);

  useEffect(() => {
    derivedCache.current = nextCache;
  }, [nextCache]);

  return result;
};

export default useEdgeLayers;
